"use client";

import { twMerge } from "tailwind-merge";
import { Icon, IconName } from "../icons/Icon";
import { colors, spacing } from "~/utils/tailwindUtils";
import Flex from "../flex/Flex";
import { useState } from "react";

export enum FormItemTypeEnum {
  Phone = "phone",
  TextArea = "textarea",
  Counter = "counter",
  HourPicker = "hourPicker",
  RadioGroup = "radioGroup",
  SimpleSelect = "simpleSelect",
  Password = "password",
  Text = "text",
  Hidden = "hidden",
  WeekPicker = "weekPicker",
  Uploader = "uploader",
}

export interface FormFieldProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  required?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string;
  name?: string;
  placeholder?: string;
  classNames?: string;
  isDisabled?: boolean;
  type?: FormItemTypeEnum.Text | FormItemTypeEnum.Password;
  verified?: boolean;
  loading?: boolean;
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  /** Runs before the built-in Enter-to-blur; call e.preventDefault() to keep the focus. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** ARIA attributes for composite widgets (e.g. role="combobox"). */
  ariaProps?: React.AriaAttributes & { role?: React.AriaRole };
}
/**
 * TextInput component that allows the user to input a text value in a form
 * @param name
 * @param placeholder
 * @param onChange
 * @param onBlur
 * @param value
 * @param isDisabled
 * @param classNames
 * @param type
 * @param verified
 * @param loading
 * @returns TextInput component
 */

export const TextInput = ({
  name,
  placeholder,
  onChange,
  onBlur,
  value,
  isDisabled,
  classNames,
  verified,
  type = FormItemTypeEnum.Text,
  loading,
  autoComplete,
  inputRef,
  autoFocus,
  onFocus,
  onClick,
  onKeyDown,
  ariaProps,
}: Omit<FormFieldProps, "label">) => {
  const [showPassword, setShowPassword] = useState(false);
  const handleTogglePassword = () => setShowPassword(!showPassword);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    // Handle Enter key - trigger blur to save the value (useful for inline editing in tables)
    if (e.keyCode === 13 || e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <Flex className="relative">
      <input
        {...ariaProps}
        ref={inputRef}
        name={name}
        id={name}
        type={showPassword ? FormItemTypeEnum.Text : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onClick={onClick}
        className={twMerge(
          "bg-gray-50 hover:bg-gray-100 px-3 h-10 text-black border border-gray-300 rounded-lg text-xs md:text-sm font-medium w-full max-w-5xl focus:outline-none focus:border-gray-300  placeholder:text-xs",
          isDisabled && "opacity-50 text-gray-500 font-normal hover:bg-gray-50 cursor-not-allowed",
          classNames
        )}
      />
      {!loading && typeof verified === "boolean" ? (
        <Icon
          name={verified ? IconName.CheckCircle : IconName.CancelCircle}
          color={verified ? colors.green[500] : colors.red[500]}
          size={spacing[6]}
          className="absolute right-2 top-2 border-l border-gray-200 pl-2"
        />
      ) : loading ? (
        <Icon
          name={IconName.Loader}
          size={spacing[5]}
          color={colors.gray[500]}
          className="absolute right-2 top-3  animate-spin"
        />
      ) : null}
      {type === FormItemTypeEnum.Password && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <Icon
            className="cursor-pointer"
            onClick={handleTogglePassword}
            name={showPassword ? IconName.EyeClosed : IconName.EyeOpen}
            aria-label={showPassword ? "hide password" : "reveal password"}
          />
        </div>
      )}
    </Flex>
  );
};
